import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'

/**
 * Placeholder surface shown while content loads.
 * Size via Tailwind classes on the host (`h-4`, `w-full`) — the only sizing API.
 * The corner comes from `shape`; a ladder class on the host (`rounded-xs`, `rounded-lg`,
 * `rounded-xl`, `rounded-full`) overrides it — an outer-tree rule outranks `:host`.
 */
@customElement('schmancy-skeleton')
export class SchmancySkeleton extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				/* Consumers size via Tailwind; provide a visible fallback height */
				min-height: 1rem;
				border-radius: var(--schmancy-radius-lg, 0.625rem);
			}
			:host([shape='text']) {
				min-height: 1em;
				border-radius: var(--schmancy-radius-xs, 0.4375rem);
			}
			:host([shape='circle']) {
				aspect-ratio: 1;
				border-radius: var(--schmancy-radius-circle, 50%);
			}
			.surface {
				width: 100%;
				height: 100%;
				min-height: inherit;
				border-radius: inherit;
				/* Token-only fill: surface-on at ~13% opacity — adapts to every color scheme automatically. */
				background: color-mix(in oklab, var(--schmancy-sys-color-surface-on) 13%, transparent);
				animation: sk-pulse 1.6s ease-in-out infinite;
			}
			@keyframes sk-pulse {
				0%,
				100% {
					opacity: 1;
				}
				50% {
					opacity: var(--sk-min, 0.45);
				}
			}
			@media (prefers-reduced-motion: reduce) {
				.surface {
					animation: none;
				}
			}
		`,
	]

	@property({ type: String, reflect: true }) shape: 'rect' | 'circle' | 'text' = 'rect'

	connectedCallback(): void {
		super.connectedCallback()
		// Announced once by the containing preset/wrapper, never per shape.
		this.setAttribute('aria-hidden', 'true')
	}

	render() {
		return html`<div part="surface" class="surface"></div>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-skeleton': SchmancySkeleton
	}
}
